import { useEffect, useState } from "react"
import { formatDistanceToNow } from "date-fns"
import { DiscoverDomain } from "../types/discover"
import { Icon } from "@/react/ui/icon"
import { Separator } from "@/react/ui/separator"
import { DashboardActivity } from "@/types/serializers"

interface DomainEventsResponse {
  domain_events: DashboardActivity[]
  summary: {
    summary: string | null
    key_movements: boolean
  }
}

function formatRelativeDate(dateString: string): string {
  return formatDistanceToNow(new Date(dateString), { addSuffix: true })
}

function getEventIcon(eventType: string): string {
  switch (eventType) {
    case "funding_raised":
    case "funding":
      return "camera"
    case "hiring":
    case "job_posted":
      return "handshake"
    case "email_added":
      return "envelope"
    default:
      return "circle-info"
  }
}

function getEventTitle(event: DashboardActivity): string {
  const { event_type, associated_record_data } = event

  switch (event_type) {
    case "funding_added":
      if (associated_record_data?.value_usd) {
        const amount = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }).format(associated_record_data.value_usd)
        return `Raised ${amount}${associated_record_data.type ? ` in ${associated_record_data.type}` : ""}`
      }
      return "Raised funds"
    case "linkedin_job_added":
      return "Started a new round of hiring"
    case "email_added":
      return "New email address found"
    default:
      return event_type.replace(/_/g, " ")
  }
}

function getEventDescription(event: DashboardActivity): string | null {
  const { event_type, metadata, associated_record_data } = event

  switch (event_type) {
    case "funding_added":
      if (metadata?.investors && Array.isArray(metadata.investors)) {
        const investors = metadata.investors as string[]
        if (investors.length > 3) {
          return `From ${investors.slice(0, 3).join(", ")}, and ${investors.length - 3} others.`
        }
        return `From ${investors.join(", ")}.`
      }
      return null
    case "linkedin_job_added":
      if (metadata?.positions && Array.isArray(metadata.positions)) {
        const positions = metadata.positions as string[]
        if (positions.length > 2) {
          return `For ${positions.slice(0, 2).join(", ")} and more.`
        }
        return `For ${positions.join(" and ")}.`
      }
      return null
    case "email_added":
      return associated_record_data?.full_email || null
    default:
      return null
  }
}

function SignalItem({ event }: { event: DashboardActivity }) {
  const icon = getEventIcon(event.event_type)
  const title = getEventTitle(event)
  const description = getEventDescription(event)

  return (
    <div className="signal-item">
      <div className="signal-item__icon">
        <Icon icon={icon} size="sm" />
      </div>
      <div className="signal-item__content">
        <h4 className="signal-item__title">{title}</h4>
        {description && (
          <p className="signal-item__description">{description}</p>
        )}
      </div>
      <span className="signal-item__date">
        {formatRelativeDate(event.created_at)}
      </span>
    </div>
  )
}

export function DomainSidepanelSignals({ domain }: { domain: DiscoverDomain }) {
  const [events, setEvents] = useState<DashboardActivity[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetch(`/search/${domain.value}/events.json`)
      .then((response) => response.json())
      .then((data: DomainEventsResponse) => {
        setEvents(data.domain_events)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [domain.value])

  if (isLoading) {
    return (
      <section className="lead-details-content">
        <div className="signals-header">
          <span className="signals-header__count">Loading...</span>
        </div>
      </section>
    )
  }

  if (events.length === 0) {
    return (
      <section className="lead-details-content">
        <div className="signals-header">
          <span className="signals-header__count">No recent updates</span>
        </div>
      </section>
    )
  }

  return (
    <section className="lead-details-content">
      <div className="signals-header">
        <span className="signals-header__count">
          {events.length} update{events.length !== 1 ? "s" : ""} in the past 90
          days
        </span>
      </div>

      <div className="signals-list">
        {events.map((event, index) => (
          <div key={event.id}>
            <SignalItem event={event} />
            {index < events.length - 1 && (
              <Separator variant="light" className="signals-separator" />
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
